import {GRANITE_8} from '@gaia/colors';
import Button from '@gaia/components/Button';
import theme from '@gaia/theme';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import CloseButton from '@map/CloseButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import {Paper} from '@mui/material';
import clsx from 'clsx';
import {ReactNode, useCallback} from 'react';

import classes from './Sidebar.module.css';
import Typography from './SidebarTypography';

export interface SidebarHeadProps extends React.HTMLProps<HTMLDivElement> {
    onSidebarChange: (sidebar: SidebarState) => void;
    sidebarType: SidebarState['type'] | 'import';
    disableClose?: boolean;
    prevSidebar?: SidebarState;
    backButton?: JSX.Element;
    onCloseButtonClick?: () => void;
    onBackButtonClick?: () => void;
    secondaryActions?: ReactNode;
}

export function SidebarHead({
    onSidebarChange,
    sidebarType,
    children,
    prevSidebar,
    onBackButtonClick,
    backButton,
    title,
    disableClose,
    secondaryActions,
    ...props
}: SidebarHeadProps) {
    const onCloseButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (props.onCloseButtonClick) props.onCloseButtonClick();
            onSidebarChange({type: 'hidden'});
            ampli.closeSidebar({type: sidebarType});
            event.stopPropagation();
        },
        [onSidebarChange, props, sidebarType]
    );

    const showBackButton = (prevSidebar && prevSidebar.type !== 'hidden') || onBackButtonClick;

    return (
        <div
            style={{
                height: 58,
                position: 'relative',
                borderBottomColor: GRANITE_8,
                borderBottomWidth: 1,
                borderBottomStyle: 'solid',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            }}
        >
            <BackButton
                customBackButton={backButton}
                useDefault={Boolean(showBackButton && !backButton)}
                onBackButtonClick={() => {
                    if (prevSidebar) onSidebarChange(prevSidebar);
                    if (onBackButtonClick) onBackButtonClick();
                }}
            />
            <div
                style={{
                    display: 'flex',
                    flexGrow: 2,
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {title && (
                    <Typography.Body1
                        bold
                        component='h1'
                        style={{
                            flex: 1,
                            textAlign: showBackButton ? 'center' : 'left',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {title}
                    </Typography.Body1>
                )}

                {children}
            </div>

            <div
                style={{
                    display: 'flex',
                    flexGrow: 1,
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginRight: theme.spacing(-1),
                }}
            >
                {secondaryActions}
                {!disableClose && (
                    <CloseButton
                        style={{boxShadow: 'none', height: 24, width: 24}}
                        onClick={onCloseButtonClick}
                    />
                )}
            </div>
        </div>
    );
}
interface BackButtonProps extends React.HTMLProps<HTMLButtonElement> {
    onBackButtonClick: VoidFunction;
    customBackButton?: JSX.Element;
    useDefault: boolean;
}

function BackButton({onBackButtonClick, customBackButton, useDefault}: BackButtonProps) {
    if (customBackButton) {
        return customBackButton;
    } else if (useDefault) {
        return (
            <Button
                appearance='default'
                startIcon={<ArrowBackIosIcon style={{width: 14, height: 14, marginRight: 0}} />}
                onClick={onBackButtonClick}
                style={{marginLeft: theme.spacing(-1)}}
            >
                Back
            </Button>
        );
    } else {
        return null;
    }
}

export function SidebarBody({children, style, ...restProps}: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            {...restProps}
            style={{
                flex: 1,
                overflowY: 'auto',
                ...style,
            }}
        >
            {children}
        </div>
    );
}

export function SidebarFooter({children}: SidebarProps) {
    return <div className={classes.sidebarFooter}>{children}</div>;
}

export function SidebarButtonContainer({children}: SidebarProps) {
    return (
        <SidebarFooter>
            <div className={classes.buttonContainer}>{children}</div>
        </SidebarFooter>
    );
}

export type SidebarProps = React.HTMLAttributes<HTMLDivElement>;

export function Sidebar({children, style, className}: SidebarProps) {
    const sidebarClasses = clsx({
        [classes.sidebar]: true,
        [className || '']: className !== undefined,
    });

    return (
        <Paper square className={sidebarClasses} role='complementary' style={style}>
            {children}
        </Paper>
    );
}
